import React, { useState } from 'react';
import { FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';
import { WalletBalance } from '@/types/wallet';
import { formatCurrencyCompact } from '@/utils/formatters';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface BalanceCardProps {
  balance: WalletBalance | null;
  loading?: boolean;
  onRefresh?: () => void;
  showActions?: boolean;
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onTransfer?: () => void;
}

const BalanceCard: React.FC<BalanceCardProps> = ({
  balance,
  loading = false,
  onRefresh,
  showActions = false,
  onDeposit,
  onWithdraw,
  onTransfer,
}) => {
  const [showBalance, setShowBalance] = useState(true);

  if (loading || !balance) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 p-4 text-white shadow-lg dark:from-primary-700 dark:to-primary-800 sm:p-6">
        <div className="flex h-24 items-center justify-center sm:h-32">
          <LoadingSpinner size="lg" color="white" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 p-4 text-white shadow-lg dark:from-primary-700 dark:to-primary-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div>
          <p className="text-xs font-medium text-primary-100 sm:text-sm">Available Balance</p>
          <div className="mt-0.5 flex items-center gap-2 sm:mt-1 sm:gap-3">
            {showBalance ? (
              <h2 className="text-2xl font-bold leading-tight sm:text-3xl">
                {formatCurrencyCompact(balance.available, balance.currency)}
              </h2>
            ) : (
              <h2 className="text-2xl font-bold leading-tight sm:text-3xl">••••••</h2>
            )}
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-1 text-primary-100 transition-colors hover:text-white"
              aria-label={showBalance ? 'Hide balance' : 'Show balance'}
            >
              {showBalance ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="rounded-lg p-1.5 transition-colors hover:bg-white/10 sm:p-2"
            aria-label="Refresh balance"
          >
            <FiRefreshCw size={18} />
          </button>
        )}
      </div>

      <div className="mb-3 grid grid-cols-3 gap-2 border-t border-primary-500/30 pt-3 sm:mb-4 sm:gap-4 sm:pt-4">
        <div>
          <p className="mb-0.5 text-[11px] text-primary-100 sm:mb-1 sm:text-xs">Pending</p>
          <p className="text-lg font-semibold leading-tight sm:text-2xl">
            {showBalance ? formatCurrencyCompact(balance.pending, balance.currency) : '••••'}
          </p>
        </div>
        <div>
          <p className="mb-0.5 text-[11px] text-primary-100 sm:mb-1 sm:text-xs">In Escrow</p>
          <p className="text-lg font-semibold leading-tight sm:text-2xl">
            {showBalance ? formatCurrencyCompact(balance.held, balance.currency) : '••••'}
          </p>
        </div>
        <div>
          <p className="mb-0.5 text-[11px] text-primary-100 sm:mb-1 sm:text-xs">Total</p>
          <p className="text-lg font-semibold leading-tight sm:text-2xl">
            {showBalance ? formatCurrencyCompact(balance.total, balance.currency) : '••••'}
          </p>
        </div>
      </div>

      {showActions && (
        <div className="flex flex-wrap gap-2">
          {onDeposit && (
            <button
              onClick={onDeposit}
              className="min-w-[92px] flex-1 rounded-lg bg-white/20 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/30"
            >
              Deposit
            </button>
          )}
          {onWithdraw && (
            <button
              onClick={onWithdraw}
              className="min-w-[92px] flex-1 rounded-lg bg-white/20 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/30"
            >
              Withdraw
            </button>
          )}
          {onTransfer && (
            <button
              onClick={onTransfer}
              className="min-w-[92px] flex-1 rounded-lg bg-white/20 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/30"
            >
              Transfer
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BalanceCard;
